<template>
  <div>
    <div v-if="participants?.length" class="pagecontent__list">
      <div class="pagecontent__top">
        <h2 class="text-44 text-bold list-title">Участники премии</h2>
        <select
          v-if="voteEnabled && sortOptions"
          id="sort"
          v-model="sortedBy"
          class="pagecontent__sort"
          name="sort"
          placeholder="Сортировка"
        >
          <option
            v-for="(value, key, id) of sortOptions"
            :key="id"
            :value="key"
          >
            {{ value }}
          </option>
        </select>
      </div>
      <UiSliderButton
        class="button"
        text="стать участником"
        to="/participant-form"
      />
      <!-- <UiSliderButton
        class="button"
        text="стать участником"
        @click="toggleModal(true)"
      /> -->
      <div v-if="voteEnabled" class="pagecontent__sort__selected">
        <p>Сортировка: {{ sortOptions[sortedBy] }}</p>
      </div>
      <ul class="pagecards">
        <UiPageCard
          v-for="participant in participants"
          :key="participant?.id"
          :participant="participant"
          :vote-allowed="isVoteAllowed"
          :vote-text="voteText()"
          @click="handleOpenParticipant(participant)"
        />
      </ul>

      <button
        v-if="showAll === false && totalItems > ITEMS_TO_SHOW"
        class="pagecontent__button"
        @click="showAll = !showAll"
      >
        <span>Показать ещё</span>
        <NuxtImg :src="arrowmore" class="arrow-more" />
      </button>
    </div>
    <div v-else class="pagecontent__list">
      <UiSliderButton
        class="button"
        text="стать участником"
        :to="'/participant-form'"
      />
    </div>
    <UiModal
      v-if="isModalVisible"
      :on-close="toggleModal"
      :is-visible="isModalVisible"
    >
      <ParticipantApplication :is-visible="isModalVisible" />
    </UiModal>
    <ParticipantVoteConfirmation />
  </div>
</template>
<script setup lang="ts">
import arrowmore from "@/public/img/arrow_more.svg"
import { useNominationPageStore } from "~/store/nominationPage"
import type { Participant } from "~/types/strapiContent"
import type { StrapiResponseData } from "~/types/strapiData"

const ITEMS_TO_SHOW = 100
const MODAL_OPEN_DELAY_MS = 420
const route = useRoute()
const router = useRouter()
const slug = route.params.slug as string
const participantData = await getParticipantsByNomination(slug)
const totalItems = ref<number>(0)
const showAll = ref<boolean>(false)
// console.log(participantData)
const sortOptions = {
  default: "по-умолчанию",
  new: "новые",
  old: "старые",
  voteLess: "много голосов",
  voteMore: "мало голосов",
}
const sortedBy = useCookie<string>("sortParticipantsBy", {
  default: () => "default",
})
const participants = computed(() => {
  const res =
    showAll.value === true
      ? participantData.value
      : participantData?.value?.slice(0, ITEMS_TO_SHOW)

  return (
    sortedBy.value === "default"
      ? res
      : res?.sort((a, b) => {
          let first
          let second

          switch (sortedBy.value) {
            case "new":
              first = Date.parse(
                b.attributes.participant?.data.attributes.publishedAt,
              )
              second = Date.parse(
                a.attributes.participant?.data.attributes.publishedAt,
              )
              break

            case "old":
              first = Date.parse(
                a.attributes.participant?.data.attributes.publishedAt,
              )
              second = Date.parse(
                b.attributes.participant?.data.attributes.publishedAt,
              )
              break

            case "voteLess":
              first = b.attributes.votes || 0
              second = a.attributes.votes || 0
              break

            case "voteMore":
              first = a.attributes.votes || 0
              second = b.attributes.votes || 0
              break

            default:
              first = a.attributes.order || 0
              second = b.attributes.order || 0
              break
          }

          return first - second
        })
  ) as StrapiResponseData<Participant>[] | null | undefined
})

const store = useNominationPageStore()
type ParticipantListItem = StrapiResponseData<Participant>
const isModalVisible = ref(false)
const toggleModal = (value: boolean) => {
  isModalVisible.value = value
}
const findParticipantById = (participantId: string) => {
  return participantData.value?.find(
    (participant) => String(participant?.id) === participantId,
  ) as ParticipantListItem | undefined
}
const handleOpenParticipant = async (participant: ParticipantListItem) => {
  store.handleOpenPreview(participant)

  const participantHash = `#${participant.id}`
  if (route.hash === participantHash) return

  await router.replace({ hash: participantHash })
}
const openParticipantByHash = async () => {
  if (!import.meta.client) return

  const participantId = route.hash.replace("#", "").trim()
  if (!participantId) {
    if (store.showModal) {
      store.handleClosePreview()
    }
    return
  }

  const participant = findParticipantById(participantId)
  if (!participant) return
  const participantHash = `#${participantId}`

  if (
    !showAll.value &&
    (participantData.value?.length || 0) > ITEMS_TO_SHOW
  ) {
    showAll.value = true
    await nextTick()
  }

  const participantCard = document.getElementById(participantId)
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches
  if (participantCard) {
    participantCard.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "center",
    })
  }

  const activeModalId = String(
    (store.dataModal as ParticipantListItem | undefined)?.id || "",
  )
  if (!store.showModal || activeModalId !== participantId) {
    if (participantCard && !reducedMotion) {
      await new Promise((resolve) => setTimeout(resolve, MODAL_OPEN_DELAY_MS))
      if (route.hash !== participantHash) return
    }

    store.handleOpenPreview(participant)
  }
}

onMounted(() => {
  openParticipantByHash()
})

watch(
  () => route.hash,
  () => {
    openParticipantByHash()
  },
)

const { voteEnabled, loggedIn, votedFor } = useAuth()
const isVoteAllowed = computed(() => {
  return (
    voteEnabled.value &&
    loggedIn.value &&
    !votedFor().value.find((e) => e.nomination === slug)
  )
})
const voteText = () => {
  if (!loggedIn.value) return "Войдите, чтобы голосовать"
  if (!isVoteAllowed.value) return "Вы проголосовали"

  return "Голосовать"
}
</script>
<style lang="scss">
.pagecontent {
  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1.45em;
    margin-bottom: 60px;

    @include sm {
      margin-bottom: 30px;
    }
  }
  &__sort {
    padding: 1em 0;
    border: none;
    border-bottom: 1px solid black;
    width: 310px;
    max-width: 100%;

    &:focus,
    &:focus-visible,
    &:focus-within {
      outline: none;
    }
    @include sm {
      width: 100%;
    }

    &__selected {
      margin-bottom: 1.45em;
    }
  }
  &__list {
    display: flex;
    flex-direction: column;

    .list-title {
      margin: 0;
    }
    .button {
      margin-bottom: 60px;

      @include sm {
        margin-bottom: 40px;
      }
    }
  }
  &__button {
    margin: 0 auto;
    color: $color-dark-gray;
    font-size: 12px;
    line-height: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    span {
      margin: 0 0 10px 0;
    }
    &:hover .arrow-more {
      animation: button-animation;
      animation-duration: 0.365s;
      animation-timing-function: ease-in-out;
    }
  }
}
.pagecards {
  margin-bottom: 60px;
  display: grid;
  grid-template-columns: repeat(2,minmax(0,1fr));
  justify-content: center;
  gap: 20px;

//   @include md {
//     grid-template-columns: repeat(auto-fill, minmax(326px, 1fr));
//   }
  @include sm {
    margin-bottom: 40px;
    grid-template-columns: repeat(auto-fill, minmax(343px, 1fr));
  }
}
@keyframes button-animation {
  0% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(0, 5px);
  }
  100% {
    transform: translate(0, 0px);
  }
}
</style>
